import { sortBy } from "lodash/fp";
import { atom, selector, selectorFamily } from "recoil";

import {
  CartCalculationModel,
  CartStatus,
  GetCartByIdDocument,
  GetCartsByFilterDocument
} from "graphql/generated";
import graphQLClient from "graphql/react-query";
import { CartSummaryInterface } from "src/types/cart.d";
import { CheckoutTypes } from "src/types/checkout";
import { OrderInterface } from "src/types/order.d";

import { orderByIdState } from "./bookingState";
import keys from "./constants";
import { patientState, routerChangeState } from "./generalState";

export const cartState = atom<any>({
  key: keys.CART_STATE,
  default: { cartItems: null, isLoading: true }
});

export const cartItemsState = selector<any>({
  key: keys.CART_ITEMS_STATE,
  get: ({ get }) => {
    const cart = get(cartState);

    return cart?.cartItems ?? [];
  }
});

export const cartItemQuantityState = selector<any>({
  key: keys.CART_ITEM_QUANTITY_STATE,
  get: ({ get }) => {
    const cart = get(cartState);

    return cart?.cartItems?.length ?? 0;
  }
});

export const orderSummaryState = selector<CheckoutTypes & { isLoading: boolean }>({
  key: keys.ORDER_SUMMARY_STATE,
  get: ({ get }) => {
    const cart = get(cartState);
    const { isLoading } = cart;
    const items = get(cartItemsState);
    const sortedItems = sortBy("product.name", items); // Sort it so the order doesn't get messed up
    const summary = get(orderCalculationState);

    return { items: sortedItems, isLoading, summary };
  }
});

export const addedProductsState = selector<string[]>({
  key: keys.ADDED_PRODUCTS_STATE,
  get: ({ get }) => {
    const items = get(cartItemsState);

    return items?.map((item) => item.product.id) ?? [];
  }
});

export const isBuyNowState = atom<boolean>({
  key: keys.IS_BUY_NOW_STATE,
  default: false
});

export const ordersInCartState = selectorFamily<Array<OrderInterface>, string>({
  key: "ORDERS_IN_CART_STATE",
  get:
    (cartId) =>
    async ({ get }) => {
      get(routerChangeState);
      const cart = get(cartByIdState(cartId));

      if (
        !cart ||
        !cart.cartItems ||
        !Array.isArray(cart.cartItems) ||
        cart.cartItems.length === 0
      ) {
        return [];
      }

      const orderIds = cart.cartItems?.map((item) => item.orderId).filter((item) => item);

      const orders = [];
      if (orderIds && orderIds.length > 0) {
        for (let i = 0; i < orderIds.length; i++) {
          const order = get(orderByIdState(orderIds[i]));
          if (order) orders.push(order);
        }

        return orders;
      }
      return orders;
    }
});

export const orderCalculationState = atom<CartSummaryInterface>({
  key: keys.ORDER_CALCULATION_STATE,
  default: {
    subTotal: 0,
    subTotalLater: 0,
    subTotalNow: 0,
    total: 0,
    totalNow: 0,
    totalLater: 0,
    totalTaxes: 0,
    taxesNow: 0,
    taxesLater: 0,
    teleConsultFee: 0,
    shippingFee: 0,
    discountAmountNow: 0,
    discountAmountLater: 0,
    paymentIntentAmount: 0,
    paymentIntentAmountBeforeTax: 0,
    cashbackEarnRate: 0,
    promotionCode: ""
  }
});

export const paymentInfoState = atom<any>({
  key: keys.PAYMENT_INFO_STATE,
  default: {
    paymentOption: "default",
    paymentId: ""
  }
});

export const isCartUpdatingState = atom<boolean>({
  key: keys.IS_CART_UPDATING_STATE,
  default: false
});

export const promoCodeState = atom<any>({
  key: keys.PROMO_CODE_STATE,
  default: null
});

export const cartByIdState = selectorFamily<CartCalculationModel, string>({
  key: keys.CART_BY_ID_STATE,
  get:
    (sysId) =>
    async ({ get }) => {
      get(routerChangeState);

      if (!sysId) return null;
      // eslint-disable-next-line no-useless-catch
      try {
        const cartData = await graphQLClient.request(GetCartByIdDocument, {
          input: {
            id: sysId
          }
        });

        return cartData.getCartById;
      } catch (error) {
        throw error;
      }
    }
});

export const OrderProductMappingState = selector({
  key: keys.ORDER_PRODUCT_MAPPING_STATE,
  get: async ({ get }) => {
    const patient = get(patientState);

    const cartData = await graphQLClient.request(GetCartsByFilterDocument, {
      input: {
        patientId: patient.sysId,
        status: CartStatus.Success
      }
    });
    const result = cartData.getCartsByFilter;
    if (result.length !== 0) {
      const cartList: any = result;
      let orderProductMapList = [];
      cartList?.forEach((cart) => {
        const cartItems = cart.cartItems?.map((item) => ({
          orderId: item.orderId,
          product: item.product
        }));
        orderProductMapList = [...orderProductMapList, ...cartItems];
      });
      return orderProductMapList;
    }
    return null;
  }
});

export const retryMethodState = atom<any>({
  key: keys.RETRY_METHOD_STATE,
  default: null
});
